import {
  destroy,
  getRoot,
  Instance,
  types,
  getSnapshot,
  applySnapshot,
  isAlive,
} from "mobx-state-tree";
import {
  interactiveGotoTypes,
  modTypes,
  interactiveConcatJumpTypes,
  findLayersOrLayerAssets,
  transformChangeTypes,
  interactiveLocationActions,
  interactiveLocationApps,
  interactiveLocationTravelModes,
  interactiveCalendarApps,
  interactiveCalendarTimeZoneModes,
} from "@blings/blings-player";
import { autorun, toJS } from "mobx";
import { throttle } from "lodash";
import _ from "lodash";
import { RootInstance } from "./main";
import type { PlayerEvents } from "@blings/blings-player";
import type { MotionStructuralConnectorAction } from "@blings/blings-player/editor-tools";
import { snapshotsConsts } from "./snapshotConsts";
import { Mod } from "../API";
import { checkModDataConnectionErrors } from "../components/mods/checkModErrors";
import { updateObjectValues } from "../helpers/JsonSearch";
import { mainAssetKey } from "./PlayerStore";
import {
  prefillLayerFromInspector as applyInspectorPrefill,
  resolveInspectorPrefillPrimarySelection,
} from "../helpers/layerInspectorPrefill";
import type { LayerInspectorSelection } from "../helpers/layerInspectorTools";
import {
  MOD_TYPE_VALUES_WITH_TRIM_EXTEND,
  TRIM_EXTEND_TYPE,
} from "../helpers/modTypeConsts";
import {
  DynamicReferenceScope,
  getRenamedModDataFields,
} from "../helpers/modKeyReferenceRename";
import { validateLocationMod } from "../components/mods/locationConnector";
import { validateCalendarMod } from "../components/mods/calendarConnector";

export const PLAYGROUND_CONTROL = "PLAYGROUND_CONTROL";

const interactiveAppValues = Array.from(
  new Set([
    ...Object.values(interactiveLocationApps),
    ...Object.values(interactiveCalendarApps),
  ])
) as Array<interactiveLocationApps | interactiveCalendarApps>;

type DuplicateConnectorTarget = {
  assetId?: string;
  layerName: string;
  layerUid?: number;
};

type ConnectorTargetRef = {
  assetId?: string;
  layerName?: string;
  layerUid?: number;
};

const COUNTDOWN_LAYER_TARGETS = [
  "days",
  "hours",
  "minutes",
  "seconds",
] as const;

type LayerAndAdditionalsTarget = ConnectorTargetRef & {
  additionals?: ConnectorTargetRef[];
};

type OptionalLayerAndAdditionalsTarget =
  | LayerAndAdditionalsTarget
  | null
  | undefined;

type ModConnectorTargetContainer = LayerAndAdditionalsTarget & {
  timeFormatAndLayers?: Partial<
    Record<
      (typeof COUNTDOWN_LAYER_TARGETS)[number],
      OptionalLayerAndAdditionalsTarget
    >
  >;
  showWhenExpired?: OptionalLayerAndAdditionalsTarget;
  hideWhenExpired?: OptionalLayerAndAdditionalsTarget;
};

function connectorTargetMatches(
  current: ConnectorTargetRef,
  target: ConnectorTargetRef
): boolean {
  const uidMatches =
    typeof current.layerUid === "number" &&
    typeof target.layerUid === "number" &&
    current.layerUid === target.layerUid;
  const assetMatches =
    !current.assetId || !target.assetId || current.assetId === target.assetId;

  return (
    uidMatches ||
    (assetMatches &&
      Boolean(current.layerName) &&
      current.layerName === target.layerName)
  );
}

function applyConnectorTarget(
  current: ConnectorTargetRef,
  target: ConnectorTargetRef
): void {
  current.assetId = target.assetId;
  current.layerName = target.layerName;
  current.layerUid = target.layerUid;
}

function updateConnectorTargetRef(
  current: ConnectorTargetRef | undefined,
  sourceTarget: ConnectorTargetRef,
  nextTarget: ConnectorTargetRef
): number {
  if (!current || !connectorTargetMatches(current, sourceTarget)) {
    return 0;
  }

  applyConnectorTarget(current, nextTarget);
  return 1;
}

function updateConnectorTargetRefs(
  targets: ConnectorTargetRef[] | undefined,
  sourceTarget: ConnectorTargetRef,
  nextTarget: ConnectorTargetRef
): number {
  return (
    targets?.reduce(
      (count, target) =>
        count + updateConnectorTargetRef(target, sourceTarget, nextTarget),
      0
    ) ?? 0
  );
}

function updateLayerAndAdditionalsTarget(
  target: OptionalLayerAndAdditionalsTarget,
  sourceTarget: ConnectorTargetRef,
  nextTarget: ConnectorTargetRef
): number {
  if (!target) return 0;

  return (
    updateConnectorTargetRef(target, sourceTarget, nextTarget) +
    updateConnectorTargetRefs(target.additionals, sourceTarget, nextTarget)
  );
}

function updateModConnectorTargets(
  moddata: ModConnectorTargetContainer,
  sourceTarget: ConnectorTargetRef,
  nextTarget: ConnectorTargetRef
): number {
  let updatedCount = updateLayerAndAdditionalsTarget(
    moddata,
    sourceTarget,
    nextTarget
  );

  COUNTDOWN_LAYER_TARGETS.forEach((targetKey) => {
    updatedCount += updateLayerAndAdditionalsTarget(
      moddata.timeFormatAndLayers?.[targetKey],
      sourceTarget,
      nextTarget
    );
  });

  updatedCount += updateLayerAndAdditionalsTarget(
    moddata.showWhenExpired,
    sourceTarget,
    nextTarget
  );
  updatedCount += updateLayerAndAdditionalsTarget(
    moddata.hideWhenExpired,
    sourceTarget,
    nextTarget
  );

  return updatedCount;
}

function getConnectorTargetFamily(
  target: OptionalLayerAndAdditionalsTarget,
  sourceTarget: ConnectorTargetRef
): LayerAndAdditionalsTarget | undefined {
  if (!target) return undefined;

  if (connectorTargetMatches(target, sourceTarget)) {
    return target;
  }

  return target.additionals?.some((additional) =>
    connectorTargetMatches(additional, sourceTarget)
  )
    ? target
    : undefined;
}

function getDuplicateConnectorTargetFamily(
  moddata: ModConnectorTargetContainer,
  sourceTarget: ConnectorTargetRef
): LayerAndAdditionalsTarget | undefined {
  const rootTarget = getConnectorTargetFamily(moddata, sourceTarget);
  if (rootTarget) return rootTarget;

  for (const targetKey of COUNTDOWN_LAYER_TARGETS) {
    const target = getConnectorTargetFamily(
      moddata.timeFormatAndLayers?.[targetKey],
      sourceTarget
    );
    if (target) return target;
  }

  return (
    getConnectorTargetFamily(moddata.showWhenExpired, sourceTarget) ??
    getConnectorTargetFamily(moddata.hideWhenExpired, sourceTarget)
  );
}

function connectorTargetListIncludes(
  targets: ConnectorTargetRef[],
  target: ConnectorTargetRef
): boolean {
  return targets.some((entry) => connectorTargetMatches(entry, target));
}

export const publicPlayerEvents: PlayerEvents[] = [
  "onAllReady",
  "onFirstPlay",
  "onPlay",
  "onFrame",
  "onReplay",
  "onPause",
  "onMute",
  "onUnmute",
  "onComplete",
  "onReplaceAnimation",
  "onSceneChange",
];
const LayerAndAdditionals = types.model({
  assetId: types.maybe(types.string),
  layerName: types.maybe(types.string),
  layerUid: types.maybe(types.number),
  isDisabled: types.optional(types.boolean, false),
  additionals: types.maybe(
    types.array(
      types.model({
        assetId: types.maybe(types.string),
        layerName: types.string,
        layerUid: types.maybe(types.number),
      })
    )
  ),
});

const ModDataModel = types.model({
  type: types.enumeration<modTypes>(
    "modType",
    MOD_TYPE_VALUES_WITH_TRIM_EXTEND
  ),
  isDisabled: types.optional(types.boolean, false),
  // IChangeInPathMod
  assetId: types.maybe(types.string),
  layerName: types.maybe(types.string),
  layerUid: types.maybe(types.number),
  additionals: types.maybe(
    types.array(
      types.model({
        assetId: types.maybe(types.string),
        layerName: types.string,
        layerUid: types.maybe(types.number),
      })
    )
  ),

  // IDynamicChangeMod
  dataKey: types.maybe(types.string),
  liveControlKey: types.maybe(types.string),
  value: types.maybe(types.frozen()),
  placeholder: types.maybe(types.frozen()), // Add the placeholder property to the mods storage
  inputName: types.maybe(types.string),
  expression: types.maybe(types.string),
  defaultValue: types.maybe(types.frozen()),

  // IInteractiveMod
  event: types.maybe(types.string),
  gotoType: types.maybe(
    types.enumeration<interactiveGotoTypes>([
      ...Object.values(interactiveGotoTypes),
    ])
  ),
  jumpType: types.maybe(
    types.enumeration<interactiveConcatJumpTypes>([
      ...Object.values(interactiveConcatJumpTypes),
    ])
  ),
  action: types.maybe(
    types.enumeration<interactiveLocationActions>([
      ...Object.values(interactiveLocationActions),
    ])
  ),
  app: types.maybe(
    // Location and Calendar share the serialized `app` field, so their values
    // intentionally live in one model enum; each connector form constrains it.
    types.enumeration<interactiveLocationApps | interactiveCalendarApps>(
      interactiveAppValues
    )
  ),
  timeZoneMode: types.maybe(
    types.enumeration<interactiveCalendarTimeZoneModes>([
      ...Object.values(interactiveCalendarTimeZoneModes),
    ])
  ),
  timeZone: types.maybe(types.string),
  travelMode: types.maybe(
    types.enumeration<interactiveLocationTravelModes>([
      ...Object.values(interactiveLocationTravelModes),
    ])
  ),
  zoom: types.maybe(types.number),
  transformChangeType: types.maybe(
    types.enumeration<transformChangeTypes>([
      ...Object.values(transformChangeTypes),
    ])
  ),
  jumpValue: types.maybe(
    types.union(types.string, types.number, types.frozen(), types.undefined)
  ),

  // ITextChangeMod
  maxChars: types.maybe(types.number),
  verticalAlign: types.maybe(types.string),
  fontSize: types.maybe(types.number),
  caseAdjustment: types.maybe(types.string),
  center: types.maybe(types.boolean),
  dontTrim: types.maybe(types.boolean),
  autoRTL: types.maybe(types.boolean),
  fixArabic: types.maybe(types.boolean),
  isAbsolute: types.maybe(types.boolean),

  // IColorChangeMod + ICustomChangeMod
  path: types.maybe(types.string),
  shapeName: types.maybe(types.string),

  //IThemeColorChangeMod
  froms: types.maybe(
    types.array(types.union(types.array(types.number), types.string))
  ),
  isLayers: types.maybe(types.boolean),
  //IAssetChangeMod
  changeSceneDurationToMediaDuration: types.optional(types.boolean, false),
  imageName: types.maybe(types.string),
  align: types.maybe(types.string),
  slice: types.maybe(types.boolean),

  // MediaMods
  startOnMarker: types.maybe(types.string),
  startAnimationFrame: types.maybe(types.number),
  stopAnimationFrame: types.maybe(types.number),
  offsetInSeconds: types.maybe(types.number),
  shouldLoop: types.maybe(types.boolean),

  //IBGVideoMod
  style: types.maybe(types.string),
  fullScreenStyle: types.maybe(types.string),
  unmute: types.maybe(types.boolean),

  //IBGAudioMod
  volume: types.maybe(types.number),

  // IFontMod
  fontConfig: types.maybe(types.frozen()),

  // IFunctionParam (IInteractiveModJSExpression + IOnPlayerEvent)
  functionString: types.maybe(types.string),
  /*
   *  following two are currently not in studio (mods stored online) just in manual player invocation
   *  functionKey: types.maybe(types.string)
   *  jsFunction:
   */

  playerEvent: types.maybe(
    types.enumeration<PlayerEvents>("PlayerEvents", publicPlayerEvents)
  ),

  //transition ITransitionTimingMod
  ip: types.maybe(types.number),
  op: types.maybe(types.number),

  exposeToPlatform: types.optional(
    types.maybe(types.maybeNull(types.boolean)),
    null
  ),

  //name for goto connectors
  ctaName: types.maybe(types.string),
  inputKeys: types.maybe(types.array(types.string)),
  sendToThirdParty: types.maybe(types.boolean),
  isMultiline: types.maybe(types.boolean),

  // ICountdownChangeMod
  timeFormatAndLayers: types.maybe(
    types.model({
      days: types.maybeNull(LayerAndAdditionals),
      hours: types.maybeNull(LayerAndAdditionals),
      minutes: types.maybeNull(LayerAndAdditionals),
      seconds: types.maybeNull(LayerAndAdditionals),
    })
  ),
  showWhenExpired: types.maybe(
    types.model({
      assetId: types.maybe(types.string),
      layerName: types.maybe(types.string),
      layerUid: types.maybe(types.number),
      additionals: types.maybe(
        types.array(
          types.model({
            assetId: types.maybe(types.string),
            layerName: types.string,
            layerUid: types.maybe(types.number),
          })
        )
      ),
    })
  ),
  hideWhenExpired: types.maybe(
    types.model({
      assetId: types.maybe(types.string),
      layerName: types.maybe(types.string),
      layerUid: types.maybe(types.number),
      additionals: types.maybe(
        types.array(
          types.model({
            assetId: types.maybe(types.string),
            layerName: types.string,
            layerUid: types.maybe(types.number),
          })
        )
      ),
    })
  ),
  hasPadding: types.maybe(types.boolean),

  // Variant optimization
  experimentId: types.maybe(types.string),
  isMainCta: types.maybe(types.boolean),
});
const ModModel = types
  .model({
    id: types.identifierNumber,
    name: types.maybe(types.union(types.maybeNull(types.string))),
    origin: types.optional(types.maybe(types.maybeNull(types.string)), null),
    // moddata: types.frozen<IMods>(),
    // disabled: types.optional(types.boolean, false),
    moddata: ModDataModel,
    description: types.maybe(types.maybeNull(types.string)),
  })
  .actions((self) => ({
    updateName(name: string) {
      self.name = name;
    },
    updateDescription(description: string) {
      self.description = description;
    },
  }));

export type IModModel = Instance<typeof ModModel>;
type MultiConnectedLayers = {
  [layer: string]: {
    [key in "text" | "themeColor" | "asset"]: Array<IModModel>;
  };
};
export const ModsModel = types
  .model({
    activeModId: types.optional(types.string, ""),
    hoveredControlModId: types.optional(types.string, ""),
    // mods: types.map(types.frozen<IMods>()), [])
    mods: types.array(ModModel),
    unchangedMods: types.maybeNull(types.string),
    trigger: types.optional(types.boolean, false),
    multiConnectedLayers: types.optional(
      types.frozen<MultiConnectedLayers>(),
      {}
    ),
    // object. mod id as key, value is an array of errors
    modsWithDataErrors: types.optional(
      types.frozen<Record<number, string[]>>(),
      {}
    ),
    modsWithBrokenLayers: types.optional(
      types.frozen<Record<number, string[]>>(),
      {}
    ),
    modsWithMultipleDynamicLength: types.optional(
      types.frozen<Record<number, string[]>>(),
      {}
    ),
    modsWithMissingProperties: types.optional(
      types.frozen<Record<number, string[]>>(),
      {}
    ),
  })
  .views((self) => ({
    get JsonConfig() {
      // Read trigger to ensure reactivity when scalar values are updated
      // const _ = self.trigger;
      return self.mods
        .filter((mod) => {
          // Guard against destroyed nodes
          if (!isAlive(mod)) {
            return false;
          }
          // Skip trim/extend mods - they're applied directly to the JSON, not as SDK mods
          if (mod.moddata.type === TRIM_EXTEND_TYPE) {
            return false;
          }
          // Skip transform mods without a valid layer name - they cause SDK errors
          if (mod.moddata.type === modTypes.transform) {
            return mod.moddata.layerName && mod.moddata.layerName.length > 0;
          }
          return true;
        })
        .map((mod) => ({
          id: mod.id,
          dataStr: JSON.stringify(mod.moddata),
          origin: mod.origin,
          name: mod.name,
          description: mod.description,
        }));
    },
    get hasUnsavedChanges() {
      const currentMods =
        self.mods.map((mod) => ({
          id: mod.id,
          dataStr: JSON.stringify(mod.moddata),
          name: mod.name,
          origin: mod.origin,
          description: mod.description,
        })) || [];
      const {
        experimentStore: { hasUnsavedChanges: hasUnsavedChangesExperiments },
      } = getRoot<any>(self);
      return (
        !_.isEqual(JSON.parse(self.unchangedMods || "[]"), currentMods) ||
        hasUnsavedChangesExperiments
      );
    },
    get hasBlockingConnectorErrors() {
      return self.mods.some((mod) => {
        const errors =
          mod.moddata.type === modTypes.interactiveLocation
            ? validateLocationMod(mod.moddata as any)
            : mod.moddata.type === modTypes.interactiveCalendar
            ? validateCalendarMod(mod.moddata as any)
            : [];
        return (
          errors.length > 0 ||
          ((mod.moddata.type === modTypes.interactiveLocation ||
            mod.moddata.type === modTypes.interactiveCalendar) &&
            Boolean(self.modsWithDataErrors[mod.id]))
        );
      });
    },
    get modIdsWithMultiConnectedLayers() {
      const mods: Array<IModModel> = [];
      Object.keys(self.multiConnectedLayers).forEach((layer) => {
        Object.keys(self.multiConnectedLayers[layer]).forEach((type) => {
          self.multiConnectedLayers[layer][type].forEach((mod) => {
            mods.push(mod);
          });
        });
      });
      return new Set(mods.map((mod) => mod.id));
    },
    getThrottleJsonConfig: throttle(
      () => {
        return JSON.parse(JSON.stringify(self.mods.map((mod) => mod.moddata)));
      },
      1000,
      { trailing: true, leading: false }
    ),
  }))
  .views((self) => ({
    hasMultiConnectedLayers: (modId: number) => {
      return self.modIdsWithMultiConnectedLayers.has(modId);
    },
    /* hasDataErrors: (modId: number) => {
      return !!self.modsWithDataErrors[modId];
    }, */
  }))
  .actions((self) => ({
    checkMultiConnectedLayers() {
      const modsGroupedByLayer = self.mods
        .filter((mod) => isAlive(mod) && !mod.moddata.isDisabled)
        .reduce((acc, mod) => {
          if (
            mod.moddata.type !== "text" &&
            mod.moddata.type !== "themeColor" &&
            mod.moddata.type !== "asset"
          )
            return acc;
          if (mod.moddata.type === "text" && !mod.moddata.layerName) return acc;
          if (mod.moddata.type === "asset" && !mod.moddata.imageName)
            return acc;
          if (
            mod.moddata.type === "themeColor" &&
            mod.moddata.isLayers &&
            !mod.moddata.layerName
          )
            return acc;
          const layers =
            mod.moddata.type === "text"
              ? [
                  mod.moddata.layerUid ||
                    `${mod.moddata.layerName}_${mod.moddata.assetId}`,
                  ...(mod.moddata.additionals?.map(
                    (additional) =>
                      additional.layerUid ||
                      `${additional.layerName}_${additional.assetId}`
                  ) || []),
                ]
              : mod.moddata.type === "asset"
              ? [mod.moddata.imageName]
              : mod.moddata.type === "themeColor"
              ? [
                  ...(mod.moddata.isLayers && mod.moddata.froms
                    ? [
                        ...mod.moddata.froms.map((colorArray) => [
                          (mod.moddata.layerName || "") +
                            (mod.moddata.layerUid || "") +
                            mod.moddata.assetId +
                            colorArray?.toString(),
                          ...(mod.moddata.additionals?.map(
                            (add) =>
                              (add.layerName || "") +
                              (add.layerUid || "") +
                              add.assetId +
                              colorArray?.toString()
                          ) || []),
                        ]),
                      ]
                    : mod.moddata.froms?.map((colorArray) => {
                        return colorArray;
                      }) || []),
                ]
              : [];
          layers.forEach((layer) => {
            acc[layer] = acc[layer] || {};
            acc[layer][mod.moddata.type] = acc[layer][mod.moddata.type] || [];
            acc[layer][mod.moddata.type].push(mod);
          });
          return acc;
        }, {} as MultiConnectedLayers);
      Object.keys(modsGroupedByLayer).forEach((layer) => {
        const layerMods = modsGroupedByLayer[layer];
        Object.keys(layerMods).forEach((type) => {
          if (layerMods[type].length <= 1) delete layerMods[type];
        });
        if (Object.keys(layerMods).length === 0)
          delete modsGroupedByLayer[layer];
      });
      self.multiConnectedLayers = modsGroupedByLayer;
    },
    checkAllModsForBrokenLayers() {
      self.modsWithBrokenLayers = {};
      const { jsonFile } = getRoot<RootInstance>(self).playerStore;
      if (!jsonFile) return;
      self.mods.forEach((mod) => {
        this.checkModForBrokenLayers(mod);
      });
    },
    checkAllAssetModsForMultipleDynamicLength() {
      const assetMods = self.mods.filter(
        (mod) => !mod.moddata.isDisabled && mod.moddata.type === modTypes.asset
      );

      // Check if more than one asset mod has dynamic length (changeSceneDurationToMediaDuration)
      const dynamicLengthAssetMods = assetMods.filter((mod) =>
        mod.moddata.isDisabled
          ? false
          : mod.moddata.changeSceneDurationToMediaDuration
      );

      if (dynamicLengthAssetMods.length > 1) {
        const modsWithErrors: any = {};
        for (let i = 0; i < dynamicLengthAssetMods.length; i++) {
          const mod = dynamicLengthAssetMods[i];
          modsWithErrors[mod.id] = ["changeSceneDurationToMediaDuration"];
        }
        this.setModsForMultipleDynamicLength(modsWithErrors);
      } else {
        this.setModsForMultipleDynamicLength({});
      }
    },
    checkForModErrors() {
      this.checkAllModsForDataErrors();
      this.checkMultiConnectedLayers();
      this.checkAllAssetModsForMultipleDynamicLength();
    },
    setModsForMultipleDynamicLength(modsWithErrors: Record<number, string[]>) {
      self.modsWithMultipleDynamicLength = modsWithErrors;
    },
    checkModForBrokenLayers(mod: IModModel) {
      const { jsonFile } = getRoot<RootInstance>(self).playerStore;
      if (!jsonFile) return;
      const { type } = mod.moddata;
      if (
        type === modTypes.advancedConfig ||
        type === modTypes.transitionTiming ||
        type === TRIM_EXTEND_TYPE ||
        type === modTypes.onPlayerEvent
      )
        return;
      const modsWithErrors = { ...self.modsWithBrokenLayers };
      const { moddata } = mod;
      const errors: Array<string> = [];

      if (type === modTypes.asset && moddata.imageName) {
        // Match the Player's media identity rules, including legacy audio URLs.
        const original = moddata.imageName;
        const assets = jsonFile.assets || [];
        let matches = assets.filter(
          (asset) => asset.id === original || asset.p === original
        );
        if (!matches.length && original.includes("/audios/")) {
          const filename = (value?: string) =>
            /\/[^/]*?_([^/]+\.mp3)$/.exec(value || "")?.[1];
          const originalFilename = filename(original);
          if (originalFilename) {
            matches = assets.filter(
              (asset) =>
                (filename(asset.id) || filename(asset.p)) === originalFilename
            );
          }
        }
        if (!matches.length || !(matches[0].u || matches[0].p)) {
          errors.push(
            `Media asset not found: ${original}. Select the media layer again.`
          );
        }
      } else if (moddata.layerName) {
        findLayersOrLayerAssets(
          {
            jsonVid: jsonFile,
            assetId: moddata.assetId,
            layerName: moddata.layerName,
            additionals: moddata.additionals,
            layerUid: moddata.layerUid,
          },
          (error) => {
            errors.push(
              `${error.message}. Asset id: ${error.payload.assetId} Layer name: ${error.payload.layerName}`
            );
          }
        );
      }
      if (errors.length) modsWithErrors[mod.id] = errors;
      else delete modsWithErrors[mod.id];
      self.modsWithBrokenLayers = modsWithErrors;
    },

    checkAllModsForDataErrors() {
      // Prevents mobx state tree reading errors
      _.debounce(this.debouncedCheckAllModsForDataErrors, 100)();
    },

    debouncedCheckAllModsForDataErrors() {
      const { dynamicDataStore, platformStore } = getRoot<RootInstance>(self);
      const modsWithErrors = {};
      Promise.all(
        self.mods.map((mod) => {
          return checkModDataConnectionErrors(
            mod,
            dynamicDataStore,
            platformStore
          );
        })
      ).then((errors) => {
        errors.forEach((error, index) => {
          if (error) modsWithErrors[self.mods[index].id] = error;
        });
        this.setModsWithDataErrors(modsWithErrors);
      });
    },
    async checkModForDataErrors(mod: IModModel) {
      const { dynamicDataStore, platformStore } = getRoot<RootInstance>(self);
      const errors = await checkModDataConnectionErrors(
        mod,
        dynamicDataStore,
        platformStore
      );
      const modsWithErrors = { ...self.modsWithDataErrors };
      if (!errors) {
        delete modsWithErrors[mod.id];
      } else modsWithErrors[mod.id] = errors;
      this.setModsWithDataErrors(modsWithErrors);
    },
    async checkInputModNoName(mod: IModModel) {
      if (mod.moddata.type === modTypes.interactiveInput) {
        const wasMissingName = mod.id in self.modsWithMissingProperties;
        const isMissingName = !mod.moddata.value;
        if (wasMissingName !== isMissingName) {
          const modsWithErrors = { ...self.modsWithMissingProperties };
          if (isMissingName) modsWithErrors[mod.id] = ["Missing name"];
          else delete modsWithErrors[mod.id];
          this.setModsWithMissingProperties(modsWithErrors);
        }
      }
    },
    setModsWithDataErrors(modsWithErrors: Record<number, string[]>) {
      self.modsWithDataErrors = modsWithErrors;
    },
    clearPlayerReportedErrors() {
      self.modsWithBrokenLayers = {};
    },
    setModsWithMissingProperties(modsWithErrors: Record<number, string[]>) {
      self.modsWithMissingProperties = modsWithErrors;
    },
    setTrigger() {
      self.trigger = !self.trigger;
    },
    afterAttach() {
      autorun((a) => {
        if (self.JsonConfig) {
          getRoot<RootInstance>(self).playerStore.runPlayer();
        }
      });
    },
    setActiveMod(id?: string | number) {
      if (typeof id !== "undefined") {
        self.activeModId = id.toString();
      } else {
        self.activeModId = "";
      }
    },
    setHoveredControlMod(id?: string | number) {
      self.hoveredControlModId = typeof id !== "undefined" ? id.toString() : "";
    },
    // Navigate to next mod in the list (for keyboard navigation)
    selectNextMod() {
      const availableMods = self.mods.filter((mod) => {
        if (!isAlive(mod)) return false;
        return Boolean(mod?.moddata?.type);
      });
      if (availableMods.length === 0) return;

      const currentIndex = availableMods.findIndex(
        (mod) => mod.id.toString() === self.activeModId
      );
      const nextIndex =
        currentIndex < availableMods.length - 1 ? currentIndex + 1 : 0;
      self.activeModId = availableMods[nextIndex].id.toString();
    },
    // Navigate to previous mod in the list (for keyboard navigation)
    selectPreviousMod() {
      const availableMods = self.mods.filter((mod) => {
        if (!isAlive(mod)) return false;
        return Boolean(mod?.moddata?.type);
      });
      if (availableMods.length === 0) return;

      const currentIndex = availableMods.findIndex(
        (mod) => mod.id.toString() === self.activeModId
      );
      const prevIndex =
        currentIndex > 0 ? currentIndex - 1 : availableMods.length - 1;
      self.activeModId = availableMods[prevIndex].id.toString();
    },
    // addCbAfterAddingMod(cb){
    //
    // }
  }))
  .actions((self) => {
    const afterAddCB: ((id?: number) => void)[] = [];
    function addCbAfterAddingMod(cb) {
      afterAddCB.push(cb);
    }
    function addMod(
      moddata: Partial<IModModel["moddata"]>,
      afterIdx?: number,
      prefillSelections?: LayerInspectorSelection[],
      options?: { activate?: boolean; prefill?: boolean }
    ) {
      // Push snapshot to undo stack (skip if gesture is active)
      const root = getRoot<RootInstance>(self);
      if (!root.undoStore.isGestureActive) {
        root.undoStore.pushSnapshot(getSnapshot(self.mods));
      }
      localStorage.setItem(
        snapshotsConsts.mods,
        JSON.stringify(getSnapshot(self.mods))
      );
      const id = generateRandomId();
      const origin = PLAYGROUND_CONTROL;
      const snapshot =
        _.cloneDeep(moddata) ?? ({} as Partial<IModModel["moddata"]>);

      // if (typeof afterIdx === "undefined") {
      if (options?.prefill !== false) {
        prefillLayerFromInspector(
          snapshot,
          snapshot.type === modTypes.asset,
          prefillSelections
        );
      }
      // }

      const mod = { id, moddata: snapshot, origin };
      if (typeof afterIdx !== "undefined") {
        const afterIndex =
          self.mods.findIndex((mod, index) => index === afterIdx) + 1;
        // @ts-ignore
        self.mods.splice(afterIndex, 0, mod);
      } else {
        // @ts-ignore
        self.mods.unshift(mod);
      }
      if (options?.activate !== false) {
        setTimeout(() => {
          self.setActiveMod(id);
        }, 1);
      }

      if (typeof id !== "undefined" && typeof afterIdx === "undefined") {
        afterAddCB.forEach((cb) => cb(id));
      }
      // self.checkModForBrokenLayers(mod as IModModel);
      self.checkForModErrors();
      self.checkInputModNoName(mod as IModModel);
      self.setTrigger();
    }
    function prefillLayerFromInspector(
      snapshot: Partial<IModModel["moddata"]>,
      isAssetChange: boolean,
      prefillSelections?: LayerInspectorSelection[]
    ) {
      const rootStore = getRoot<RootInstance>(self);
      const {
        layerInspectorPrimarySelection,
        layerInspectorSelections,
        layerInspectorContextMenu,
        jsonFile,
      } = rootStore.playerStore;
      const activeSelections =
        prefillSelections ??
        layerInspectorContextMenu?.selections ??
        layerInspectorSelections;
      const activePrimarySelection = resolveInspectorPrefillPrimarySelection({
        primarySelection: layerInspectorPrimarySelection,
        explicitSelections: prefillSelections,
        contextMenuLayer: layerInspectorContextMenu?.layer,
      });

      applyInspectorPrefill({
        snapshot,
        isAssetChange,
        selection: activePrimarySelection,
        selections: activeSelections,
        jsonFile,
        fallbackAssetId: mainAssetKey,
      });
    }

    function generateRandomId() {
      const usedIds = new Set(self.mods.map((mod) => mod.id));
      let rnd: number;
      do {
        rnd = Math.floor(Math.random() * 100000);
      } while (usedIds.has(rnd));
      return rnd;
    }
    return { addMod, addCbAfterAddingMod };
  })
  .actions((self) => ({
    copyMod(mod: IModModel) {
      self.addMod(
        toJS(mod.moddata),
        self.mods.findIndex((m) => m.id === mod.id)
      );
    },
    removeMod(mod) {
      // Push snapshot to undo stack before removing
      const root = getRoot<RootInstance>(self);
      if (!root.undoStore.isGestureActive) {
        root.undoStore.pushSnapshot(getSnapshot(self.mods));
      }
      destroy(mod);
      self.checkMultiConnectedLayers();
      self.setTrigger();
    },
    snapshotMods() {
      const modsSnapshot = localStorage.getItem(snapshotsConsts.mods);
      if (modsSnapshot) {
        applySnapshot(self.mods, JSON.parse(modsSnapshot));
      }
    },
    // Used by UndoStore to apply snapshots during undo/redo operations
    applyModsSnapshot(snapshot: any) {
      applySnapshot(self.mods, snapshot);
      self.checkForModErrors();
      self.setTrigger();
    },
    reorderMods(mods: Array<IModModel>) {
      // Push snapshot to undo stack before reordering
      const root = getRoot<RootInstance>(self);
      if (!root.undoStore.isGestureActive) {
        root.undoStore.pushSnapshot(getSnapshot(self.mods));
      }
      self.mods.replace(mods);
      self.checkForModErrors();
    },
    renameLayerConnectorTargets(
      sourceTarget: ConnectorTargetRef,
      nextTarget: ConnectorTargetRef
    ) {
      if (!nextTarget.layerName) return 0;

      const root = getRoot<RootInstance>(self);
      if (!root.undoStore.isGestureActive) {
        root.undoStore.pushSnapshot(getSnapshot(self.mods));
      }

      let updatedCount = 0;
      self.mods.forEach((mod) => {
        const modUpdatedCount = updateModConnectorTargets(
          mod.moddata as ModConnectorTargetContainer,
          sourceTarget,
          nextTarget
        );
        if (!modUpdatedCount) return;

        mod.origin = PLAYGROUND_CONTROL;
        updatedCount += modUpdatedCount;
      });

      if (updatedCount) {
        self.checkForModErrors();
        self.setTrigger();
      }

      return updatedCount;
    },
    addDuplicateConnectorTargets(actions: MotionStructuralConnectorAction[]) {
      const duplicateActions = actions.filter(
        (action) => action.kind === "addDuplicateTarget" && action.target
      );
      if (!duplicateActions.length) return 0;

      const root = getRoot<RootInstance>(self);
      if (!root.undoStore.isGestureActive) {
        root.undoStore.pushSnapshot(getSnapshot(self.mods));
      }

      let updatedCount = 0;
      duplicateActions.forEach((action) => {
        const target = action.target;
        if (!target?.layerName) return;

        const mod = self.mods[action.connection.modIndex];
        if (!mod) return;

        const nextTarget: DuplicateConnectorTarget = {
          assetId: target.assetId,
          layerName: target.layerName,
          layerUid: target.layerUid,
        };
        const sourceTarget: ConnectorTargetRef = {
          assetId: action.sourceTarget.assetId,
          layerName: action.sourceTarget.layerName,
          layerUid: action.sourceTarget.layerUid,
        };
        const targetFamily = getDuplicateConnectorTargetFamily(
          mod.moddata as ModConnectorTargetContainer,
          sourceTarget
        );
        if (!targetFamily) return;

        const existingTargets: DuplicateConnectorTarget[] = [
          {
            assetId: targetFamily.assetId,
            layerName: targetFamily.layerName || "",
            layerUid: targetFamily.layerUid,
          },
          ...(targetFamily.additionals?.map((additional) => ({
            assetId: additional.assetId,
            layerName: additional.layerName,
            layerUid: additional.layerUid,
          })) ?? []),
        ].filter((entry) => entry.layerName);
        if (connectorTargetListIncludes(existingTargets, nextTarget)) return;

        mod.origin = PLAYGROUND_CONTROL;
        if (!targetFamily.additionals) {
          targetFamily.additionals = [] as any;
        }
        targetFamily.additionals.push(nextTarget as any);
        updatedCount += 1;
      });

      if (updatedCount) {
        self.checkForModErrors();
        self.setTrigger();
      }

      return updatedCount;
    },
    retargetMovedConnectorTargets(actions: MotionStructuralConnectorAction[]) {
      const moveActions = actions.filter(
        (action) => action.kind === "retargetMovedTarget" && action.target
      );
      if (!moveActions.length) return 0;

      const root = getRoot<RootInstance>(self);
      if (!root.undoStore.isGestureActive) {
        root.undoStore.pushSnapshot(getSnapshot(self.mods));
      }

      let updatedCount = 0;
      moveActions.forEach((action) => {
        const target = action.target;
        if (!target?.layerName) return;

        const mod = self.mods[action.connection.modIndex];
        if (!mod) return;

        const nextTarget: ConnectorTargetRef = {
          assetId: target.assetId,
          layerName: target.layerName,
          layerUid: target.layerUid,
        };
        const sourceTarget: ConnectorTargetRef = {
          assetId: action.sourceTarget.assetId,
          layerName: action.sourceTarget.layerName,
          layerUid: action.sourceTarget.layerUid,
        };
        const modUpdatedCount = updateModConnectorTargets(
          mod.moddata as ModConnectorTargetContainer,
          sourceTarget,
          nextTarget
        );
        if (!modUpdatedCount) return;

        mod.origin = PLAYGROUND_CONTROL;
        updatedCount += modUpdatedCount;
      });

      if (updatedCount) {
        self.checkForModErrors();
        self.setTrigger();
      }

      return updatedCount;
    },
    replaceMods(modsInput?: Array<Mod> | null) {
      // Clear undo history on project load/switch
      const root = getRoot<RootInstance>(self);
      root.undoStore.clearHistory();

      if (!modsInput || modsInput.length === 0) {
        self.mods.replace([]);
        self.unchangedMods = JSON.stringify([]);
        self.checkForModErrors();
        return;
      }

      let newMstMods: IModModel[];
      let unchangedSnapshotItems: {
        id: number;
        dataStr: string;
        origin: string | null | undefined;
        name: string | null | undefined;
        description?: string | null;
      }[];

      // Input is likely Mod[]
      const inputAsMod = modsInput as Mod[];
      newMstMods = inputAsMod.map((m) =>
        ModModel.create({
          id: m.id,
          moddata: JSON.parse(m.dataStr || "{}"),
          origin: m.origin,
          name: m.name,
          description: m.description,
        })
      );

      // Create the snapshot for unchangedMods from the newly created MST models.
      // Stringifying newMstMod.moddata provides a canonical JSON string of the data.
      unchangedSnapshotItems = newMstMods.map((m) => ({
        id: m.id,
        dataStr: JSON.stringify(m.moddata),
        origin: m.origin,
        name: m.name,
        description: m.description,
      }));

      self.mods.replace(newMstMods);
      self.unchangedMods = JSON.stringify(unchangedSnapshotItems);
      self.checkForModErrors();
    },
    mergeWithModsFromSubscription(mods?: Array<Mod> | null) {
      if (!mods) {
        mods = [];
      }
      this.setUnchangedMods(mods);
      const parsedMods = mods.map((mod) =>
        ModModel.create({
          id: mod.id,
          moddata: JSON.parse(mod.dataStr),
          origin: mod.origin,
          name: mod.name,
          description: mod.description,
        })
      );

      const activeMod = self.mods.find(
        (mod) => mod.id.toString() === self.activeModId
      );
      if (!activeMod) {
        self.mods.replace(parsedMods);
        return;
      }
      let replacedActiveInIncoming = false;
      for (let i = 0; i < parsedMods.length; i++) {
        const mod = parsedMods[i];
        if (mod.id === activeMod.id) {
          parsedMods[i] = activeMod;
          replacedActiveInIncoming = true;
          break;
        }
      }
      if (!replacedActiveInIncoming) {
        parsedMods.push(activeMod);
      }
      self.mods.replace(parsedMods);
      self.checkMultiConnectedLayers();
    },
    async updateInnerModValue(modId: number, keys: string[], values: any) {
      const m = self.mods.find((mod) => mod.id === modId);
      if (!m) return;
      m.origin = PLAYGROUND_CONTROL;
      updateObjectValues(m.moddata, keys, values);
      self.setTrigger();
    },
    async updateValue(modId: number, key: string | string[], value: any) {
      const m = self.mods.find((mod) => mod.id === modId);
      if (!m) return;

      // Push snapshot to undo stack (skip if gesture is active, use debounce for rapid updates)
      const root = getRoot<RootInstance>(self);
      if (!root.undoStore.isGestureActive) {
        root.undoStore.pushSnapshot(getSnapshot(self.mods), { debounce: true });
      }

      const keys = Array.isArray(key) ? key : [key];
      const values = Array.isArray(key) ? value : [value];
      m.origin = PLAYGROUND_CONTROL;
      keys.forEach((k, i) => {
        m.moddata[k] = values[i];
        if (k === "ctaName") {
          this.updateModName(modId, values[i]);
        }
      });
      // Trigger reactivity once after all keys are updated (not per-key)
      self.setTrigger();
      if (keys.includes("isDisabled")) {
        self.checkMultiConnectedLayers();
        self.checkAllAssetModsForMultipleDynamicLength();
      }
      if (keys.includes("assetId") || key === "imageName" || key === "froms") {
        self.checkMultiConnectedLayers();
      }
      if (
        keys.includes("value") ||
        key === "inputKeys" ||
        key === "jumpValue" ||
        m.moddata.type === modTypes.interactiveLocation ||
        m.moddata.type === modTypes.interactiveCalendar
      ) {
        await self.checkModForDataErrors(m);
      }
      if (m.moddata.type === modTypes.interactiveInput && key === "value") {
        self.checkAllModsForDataErrors();
        self.checkInputModNoName(m);
      }
      if (
        keys.includes("assetId") ||
        keys.includes("imageName") ||
        key === "isLayers"
      ) {
        self.checkModForBrokenLayers(m);
      }
      if (key.includes("changeSceneDurationToMediaDuration")) {
        self.checkAllAssetModsForMultipleDynamicLength();
      }
      if (key.includes("isDisabled")) {
        self.checkAllAssetModsForMultipleDynamicLength();
      }
    },
    updateModName(modId: string | number, name: string) {
      const m = self.mods.find((mod) => mod.id.toString() === modId.toString());
      if (!m) return;
      m.updateName(name);
    },
    updateModDescription(modId: string | number, description: string) {
      const m = self.mods.find((mod) => mod.id.toString() === modId.toString());
      if (!m) return;
      m.origin = PLAYGROUND_CONTROL;
      m.updateDescription(description);
    },
    setUnchangedMods(mods: Array<Mod>) {
      self.unchangedMods = JSON.stringify(mods);
    },
    updateFlowNameInMods(oldName: string, newName: string) {
      const { platformStore } = getRoot<RootInstance>(self);

      const vps = platformStore.projectWorkspaceVersion?.videoParts;
      if (vps) {
        vps.forEach((videoPart) => {
          if (videoPart.modsArr) {
            videoPart.modsArr.forEach((mod) => {
              const modData = JSON.parse(mod.dataStr || "{}");

              if (modData.type === modTypes.interactiveConcat) {
                let valueUpdated = false;

                if (
                  modData.jumpValue &&
                  typeof modData.jumpValue === "object" &&
                  modData.jumpValue.lineup === oldName
                ) {
                  modData.jumpValue = {
                    ...modData.jumpValue,
                    lineup: newName,
                  };
                  valueUpdated = true;
                }

                if (Array.isArray(modData.value)) {
                  modData.value = modData.value.map((value: any) => {
                    if (value && value.lineup === oldName) {
                      valueUpdated = true;
                      return { ...value, lineup: newName };
                    }
                    return value;
                  });
                }

                if (valueUpdated) {
                  mod.dataStr = JSON.stringify(modData);
                }
              }
            });
          }
        });
      }

      self.mods.forEach((mod) => {
        if (mod.moddata.type === modTypes.interactiveConcat) {
          if (
            mod.moddata.jumpValue &&
            typeof mod.moddata.jumpValue === "object"
          ) {
            const jumpValue = mod.moddata.jumpValue as any;
            if (jumpValue.lineup === oldName) {
              const updatedJumpValue = {
                ...jumpValue,
                lineup: newName,
              };
              this.updateValue(mod.id, "jumpValue", updatedJumpValue);
            }
          }

          if (Array.isArray(mod.moddata.value)) {
            let hasChanges = false;
            const updatedValue = mod.moddata.value.map((value: any) => {
              if (value && value.lineup === oldName) {
                hasChanges = true;
                return { ...value, lineup: newName };
              }
              return value;
            });

            if (hasChanges) {
              this.updateValue(mod.id, "value", updatedValue);
            }
          }
        }
      });

      if (platformStore.projectWorkspaceFlowDiagram) {
        platformStore.projectWorkspaceFlowDiagram.updateLineupIdReferences(
          oldName,
          newName
        );
      }

      platformStore.updateLineupIdInProjectVersion(oldName, newName);
    },
    updateDataKeyReferences(
      oldKey: string,
      newKey: string,
      scope: DynamicReferenceScope = "data"
    ) {
      self.mods.forEach((mod) => {
        const updates = getRenamedModDataFields(
          {
            dataKey: mod.moddata.dataKey,
            liveControlKey: mod.moddata.liveControlKey,
            expression: mod.moddata.expression,
            functionString: mod.moddata.functionString,
            value: mod.moddata.value,
          },
          oldKey,
          newKey,
          scope
        );

        if (typeof updates.dataKey !== "undefined") {
          this.updateValue(mod.id, "dataKey", updates.dataKey);
        }

        if (typeof updates.liveControlKey !== "undefined") {
          this.updateValue(mod.id, "liveControlKey", updates.liveControlKey);
        }

        if (typeof updates.expression !== "undefined") {
          this.updateValue(mod.id, "expression", updates.expression);
        }

        if (typeof updates.functionString !== "undefined") {
          this.updateValue(mod.id, "functionString", updates.functionString);
        }

        if (typeof updates.value !== "undefined") {
          this.updateValue(mod.id, "value", updates.value);
        }
      });

      self.checkAllModsForDataErrors();
    },

    updateNestedDataKeys(items, oldKey: string, newKey: string) {
      items.forEach((item) => {
        if (item.dataKey === oldKey) {
          item.dataKey = newKey;
        }

        if (item.children) {
          this.updateNestedDataKeys(item.children, oldKey, newKey);
        }
      });
    },
  }));

export type IModsModel = Instance<typeof ModsModel>;

export const modsStore = ModsModel.create({
  // mods: [{type: modTypes.text}]
});
